import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EmbeddedLayout from '../../components/admin/EmbeddedLayout';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Snackbar, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { io } from 'socket.io-client';
import { baseURL } from '../../config';
import timeTrackerController from '../../Api/TimeTracker';
import { useLocalStorageState } from './utils/useLocalStorageState';
import { useUser } from '../../hooks/useUser';
import { Navigate } from 'react-router-dom';
import TodayScansView from './views/TodayScansView';
import AllScansView from './views/AllScansView';
import LiveView from './views/LiveView';
import IssuesView from './views/IssuesView';
import EmployeeMonthView from './views/EmployeeMonthView';

interface TimeTrackerProps { embedded?: boolean }

type TabKey = 'today' | 'all' | 'live' | 'issues' | 'employee';

const normalizeKey = (value?: string) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const labelPlaceFromDeviceId = (deviceId?: string) => {
    const key = normalizeKey(deviceId);
    if (key === 'sklep') return 'Sklep';
    if (key === 'magazyn') return 'Magazyn';
    return deviceId || '';
};

const labelTypeFromStationId = (stationId?: string) => {
    const key = normalizeKey(stationId);
    if (key === 'wejscie' || key === 'wejście') return 'Wejście';
    if (key === 'piec') return 'Piec';
    if (key === 'przerwa') return 'Przerwa';
    if (key === 'papieros') return 'Papieros';
    return stationId || '';
};

const labelActivityFromActivityType = (activityType?: string) => {
    const key = normalizeKey(activityType);
    if (key === 'wejscie' || key === 'wejście') return 'Wejście';
    if (key === 'przerwa') return 'Przerwa';
    if (key === 'papieros') return 'Papieros';
    if (key === 'piec') return 'Piec';
    return activityType || '';
};

const TimeTracker: FC<TimeTrackerProps> = ({ embedded = false }) => {
    const user = useUser();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const authChecked = user.authReady;
    const isAdmin = user.hasPermission('timeTracking.read');

    const [tab, setTab] = useLocalStorageState<TabKey>('timeTracker:selectedTab', 'today');
    const [selectedEmployee, setSelectedEmployee] = useState<{ employeeId: string; employeeName: string | null } | null>(null);

    const [newScansCount, setNewScansCount] = useState<number>(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarText, setSnackbarText] = useState<string>('');

    const [issuesCount, setIssuesCount] = useState<number>(0);
    const [issuesBannerDismissed, setIssuesBannerDismissed] = useState(false);

    const employeeNameByUidRef = useRef<Map<string, string>>(new Map());

    const loadUidNameMap = useCallback(async () => {
        const res = await timeTrackerController.getLive();
        if (!res.ok) return;

        const map = new Map<string, string>();
        for (const e of res.employees || []) {
            const name = (e.employeeName || '').trim();
            if (!name) continue;
            for (const uid of e.uids || []) {
                const key = String(uid).trim().toUpperCase();
                if (key) map.set(key, name);
            }
        }
        employeeNameByUidRef.current = map;
    }, []);

    const refreshIssuesCount = useCallback(async () => {
        const res = await timeTrackerController.getIssues('open');
        if (!res.ok) {
            setIssuesCount(0);
            return;
        }
        setIssuesCount(res.issues?.length || 0);
    }, []);

    useEffect(() => {
        loadUidNameMap();
        refreshIssuesCount();
    }, [loadUidNameMap, refreshIssuesCount]);

    useEffect(() => {
        const socket = io(baseURL, { transports: ['websocket', 'polling'] });

        socket.on(
            'timeTracking:newScan',
            (payload: {
                scanId?: string;
                createdAt?: string;
                device_id?: string;
                station_id?: string;
                uid?: string;
                activityType?: string;
                actionPhase?: string;
            }) => {
                setNewScansCount((v) => {
                    const next = v + 1;

                    const uid = payload?.uid ? String(payload.uid).trim().toUpperCase() : '';
                    const fullName = uid ? employeeNameByUidRef.current.get(uid) : '';
                    const who = fullName ? fullName : uid ? `Karta: ${uid}` : 'Nowa karta';

                    const when = payload?.createdAt ? dayjs(payload.createdAt).format('HH:mm') : 'Teraz';
                    const place = labelPlaceFromDeviceId(payload?.device_id ? String(payload.device_id) : '');
                    const type = labelTypeFromStationId(payload?.station_id ? String(payload.station_id) : '');
                    const activity = labelActivityFromActivityType(payload?.activityType ? String(payload.activityType) : '');
                    const phase = payload?.actionPhase ? String(payload.actionPhase) : '';

                    const where = [place, type].filter(Boolean).join(' / ');
                    const what = [activity, phase].filter(Boolean).join(' ');

                    setSnackbarText(
                        [
                            `Nowe skany: ${next}`,
                            who,
                            where ? where : null,
                            what ? what : null,
                            `(${when})`,
                        ]
                            .filter(Boolean)
                            .join(' • ')
                    );
                    setSnackbarOpen(true);

                    return next;
                });

                // W tle aktualizujemy mapowanie (żeby kolejne powiadomienia miały nazwiska)
                loadUidNameMap();
                refreshIssuesCount();
            }
        );

        return () => {
            socket.disconnect();
        };
    }, [loadUidNameMap, refreshIssuesCount]);

    const tabs = useMemo(() => {
        const base: Array<{ key: TabKey; label: string }> = [
            { key: 'today', label: 'Dzisiaj' },
            { key: 'all', label: 'Wszystkie' },
            { key: 'live', label: 'Live' },
            { key: 'issues', label: issuesCount > 0 ? `Problemy (${issuesCount})` : 'Problemy' },
        ];
        if (selectedEmployee) base.push({ key: 'employee', label: 'Pracownik' });
        return base;
    }, [issuesCount, selectedEmployee]);

    const renderView = () => {
        switch (tab) {
            case 'today':
                return <TodayScansView />;
            case 'all':
                return <AllScansView onRefreshed={() => setNewScansCount(0)} />;
            case 'live':
                return (
                    <LiveView
                        onSelectEmployee={(employeeId, employeeName) => {
                            setSelectedEmployee({ employeeId, employeeName });
                            setTab('employee');
                        }}
                    />
                );
            case 'issues':
                return (
                    <IssuesView
                        onIssuesCount={(count) => {
                            setIssuesCount(count);
                            if (count === 0) setIssuesBannerDismissed(false);
                        }}
                    />
                );
            case 'employee':
                if (!selectedEmployee) return <TodayScansView />;
                return (
                    <EmployeeMonthView
                        employeeId={selectedEmployee.employeeId}
                        employeeName={selectedEmployee.employeeName}
                        onBack={() => {
                            setTab('live');
                        }}
                    />
                );
            default:
                return <TodayScansView />;
        }
    };

    if (!authChecked) {
        return (
            <EmbeddedLayout embedded={embedded} title="Sprawdzanie obecności">
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <CircularProgress size={22} />
                        <Typography>Sprawdzam autoryzację…</Typography>
                    </Stack>
                </Paper>
            </EmbeddedLayout>
        );
    }

    if (!isAdmin) return <Navigate to="/logowanie" replace />;

    return (
        <EmbeddedLayout embedded={embedded} title="Sprawdzanie obecności">
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3500}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity="info"
                    variant="filled"
                    onClose={() => setSnackbarOpen(false)}
                    sx={{ width: '100%', borderRadius: 2 }}
                >
                    {snackbarText}
                </Alert>
            </Snackbar>

            <Box sx={{ px: 1, py: 1.5 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" fontWeight={800}>
                                TimeTracker
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Widoki: Dzisiaj • Wszystkie • Live • Problemy • Raport pracownika
                            </Typography>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                            {newScansCount > 0 ? (
                                <Chip
                                    color="info"
                                    variant="outlined"
                                    label={`Nowe skany: ${newScansCount}`}
                                    sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                                />
                            ) : null}
                            {issuesCount > 0 ? (
                                <Chip
                                    color="warning"
                                    variant="outlined"
                                    label={`Problemy: ${issuesCount}`}
                                    sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                                />
                            ) : null}
                        </Stack>
                    </Stack>

                    {issuesCount > 0 && !issuesBannerDismissed ? (
                        <Box sx={{ mt: 2 }}>
                            <Alert
                                severity="warning"
                                onClose={() => setIssuesBannerDismissed(true)}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => {
                                            setTab('issues');
                                            setIssuesBannerDismissed(true);
                                        }}
                                    >
                                        Otwórz
                                    </Button>
                                }
                            >
                                Wykryto otwarte problemy: {issuesCount}. Najczęściej to brak zakończenia (stop).
                            </Alert>
                        </Box>
                    ) : null}

                    <Box sx={{ mt: 2 }}>
                        <Tabs
                            value={tab}
                            onChange={(_, next) => setTab(next)}
                            variant={isDesktop ? 'standard' : 'scrollable'}
                            scrollButtons={isDesktop ? false : 'auto'}
                        >
                            {tabs.map((t) => (
                                <Tab key={t.key} value={t.key} label={t.label} />
                            ))}
                        </Tabs>
                    </Box>
                </Paper>

                <Box sx={{ mt: 2 }}>{renderView()}</Box>
            </Box>
        </EmbeddedLayout>
    );
};

export default TimeTracker;
